/**
 * QuoteDetailPage - Quote Preview
 * PROMPT 40: Financial Documents Unified View Layer
 * PROMPT 41: Financial Communications - Email integration
 * EF-QUOTE-DETAIL-ALIGN-01: Aligned with PDF grouped structure
 * EF-PDF-HEALTH-CHECK: PDF engine status indicator
 * EF-QUOTE-SINGLE-SOURCE-OF-TRUTH-LOCK-V1: Uses build_quote_view_model for single source of truth
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getQuoteViewModel,
  getQuotePdfUrl,
  sendQuoteEmail,
  getQuoteEmailLog,
  getPdfHealthCheck,
  viewQuotePdf,
  downloadQuotePdf,
  extractErrorMessage,
} from "../lib/api";
import { useFormatters } from "../components/shared/useFormatters";
import { LoadingSpinner } from "../components/shared/DataComponents";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  DocumentHeader,
  DocumentDetails,
  GroupedLineItems,
  TotalsSummary,
  DocumentFooter,
  DocumentActions,
  EmailComposeModal,
  EmailLogSection,
  printStyles,
} from "../components/financial";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertCircle, Loader2, Download } from "lucide-react";
// EVENTSFLO-CLIENT-CREDIT-UI-FORM-V1
import AddServiceCreditDialog, { CreditHistoryTable } from "../components/financial/AddServiceCreditDialog";
// EVENTSFLO-PDF-SUBTOTAL-BEFORE-CREDITS-V1
import EditableClientNotes from "../components/financial/EditableClientNotes";
// EVENTSFLO-GENERIC-FINANCIAL-CREDIT-ADJUSTMENT-LINE-V1
import QuoteAdjustmentsPanel from "../components/financial/QuoteAdjustmentsPanel";

export default function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatDate, formatCurrency, currencySymbol: platformCurrencySymbol } = useFormatters();
  
  // EF-QUOTE-SINGLE-SOURCE-OF-TRUTH-LOCK-V1: Use view model as single source
  const [viewModel, setViewModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  
  // EF-PDF-HEALTH-CHECK: PDF engine status
  const [pdfHealthStatus, setPdfHealthStatus] = useState("checking"); // "checking" | "ok" | "error"
  const [pdfHealthMessage, setPdfHealthMessage] = useState("");
  const [pdfHealthChecking, setPdfHealthChecking] = useState(false);

  // EVENTSFLO-CLIENT-CREDIT-UI-FORM-V1
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);

  // EF-QUOTE-SINGLE-SOURCE-OF-TRUTH-LOCK-V1: Derived data from view model
  const header = viewModel?.header || {};
  const shifts = viewModel?.shifts || [];
  const totals = viewModel?.totals || {};
  const footer = viewModel?.footer || {};
  const notes = viewModel?.notes || "";
  const currencySymbol = totals.currency_symbol || platformCurrencySymbol;

  const loadQuote = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      // EF-QUOTE-SINGLE-SOURCE-OF-TRUTH-LOCK-V1: Load unified view model
      const viewModelRes = await getQuoteViewModel(id);
      setViewModel(viewModelRes.data);
      console.log("[QUOTE VIEW MODEL LOADED]", viewModelRes.data);
      console.log("[VAT CALCULATION]", {
        subtotal: viewModelRes.data.totals.subtotal,
        tax_rate: viewModelRes.data.totals.tax_rate,
        vat_amount: viewModelRes.data.totals.vat_amount,
        total: viewModelRes.data.totals.total
      });
    } catch (err) {
      console.error("Failed to load quote:", extractErrorMessage(err));
      toast.error("Failed to load quote");
      navigate("/quotes");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadEmailLogs = useCallback(async () => {
    if (!id) return;
    setEmailLogsLoading(true);
    try {
      const res = await getQuoteEmailLog(id);
      setEmailLogs(res.data || []);
    } catch (err) {
      console.error("Failed to load email logs:", extractErrorMessage(err));
    } finally {
      setEmailLogsLoading(false);
    }
  }, [id]);

  // EF-PDF-HEALTH-CHECK: Check PDF engine status
  const checkPdfHealth = useCallback(async () => {
    setPdfHealthChecking(true);
    try {
      const res = await getPdfHealthCheck();
      const data = res.data;
      if (data.status === "ok") {
        setPdfHealthStatus("ok");
        setPdfHealthMessage("");
      } else {
        setPdfHealthStatus("error");
        setPdfHealthMessage(data.message || "PDF engine unavailable");
      }
    } catch (err) {
      setPdfHealthStatus("error");
      setPdfHealthMessage("Unable to check PDF engine status");
    } finally {
      setPdfHealthChecking(false);
    }
  }, []);

  useEffect(() => {
    loadQuote();
    loadEmailLogs();
    checkPdfHealth(); // Check PDF health on page load
  }, [loadQuote, loadEmailLogs, checkPdfHealth]);

  const handleSendEmail = async (emailData) => {
    await sendQuoteEmail(id, emailData);
    // Refresh email logs after sending
    loadEmailLogs();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!viewModel) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          <p>Quote not found</p>
        </div>
      </div>
    );
  }

  // EF-QUOTE-SINGLE-SOURCE-OF-TRUTH-LOCK-V1: Totals from view model
  const pdfUrlSummary = getQuotePdfUrl(id, 'summary');
  const pdfUrlDetailed = getQuotePdfUrl(id, 'detailed');

  return (
    <div 
      className="min-h-screen bg-surface-base py-8 px-4 print:bg-white print:p-0"
      data-testid="quote-detail-page"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Actions Bar - EF-QUOTE-SINGLE-SOURCE-OF-TRUTH-LOCK-V1: Renamed to Download PDF */}
        {/* EVENTSFLO-QUOTES-PAGE-SUMMARY-DETAILED-PDF-AUTH-FIX-V1: authed blob fetchers */}
        <DocumentActions
          onBack={() => navigate("/quotes")}
          backLabel="Back to Quotes"
          pdfUrlSummary={pdfUrlSummary}
          pdfUrlDetailed={pdfUrlDetailed}
          downloadFileName={`quote-${header.document_number || id}.pdf`}
          showSendEmail={true}
          onSendEmail={() => setEmailModalOpen(true)}
          downloadLabel="Download PDF"
          onPreviewSummary={() => viewQuotePdf(id, header.document_number, 'summary')}
          onPreviewDetailed={() => viewQuotePdf(id, header.document_number, 'detailed')}
          onDownloadSummary={() => downloadQuotePdf(id, header.document_number, 'summary')}
          onDownloadDetailed={() => downloadQuotePdf(id, header.document_number, 'detailed')}
        />

        {/* EF-PDF-HEALTH-CHECK: PDF Engine Status Indicator */}
        <div className="flex items-center justify-end gap-2 text-xs print:hidden">
          <span className="text-muted-foreground">PDF Engine:</span>
          {pdfHealthChecking ? (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Checking...
            </span>
          ) : pdfHealthStatus === "ok" ? (
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle2 className="h-3 w-3" />
              Healthy
            </span>
          ) : (
            <span className="flex items-center gap-1 text-red-600" title={pdfHealthMessage}>
              <AlertCircle className="h-3 w-3" />
              Unavailable
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={checkPdfHealth}
            disabled={pdfHealthChecking}
            className="h-6 px-2 text-xs"
            title="Re-check PDF Engine"
          >
            <RefreshCw className={`h-3 w-3 ${pdfHealthChecking ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* EVENTSFLO-CLIENT-CREDIT-UI-FORM-V1 — Add service credit */}
        <div className="flex items-center justify-end print:hidden">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCreditDialogOpen(true)}
            data-testid="quote-add-service-credit-btn"
          >
            Add service credit
          </Button>
        </div>
        <AddServiceCreditDialog
          open={creditDialogOpen}
          onOpenChange={setCreditDialogOpen}
          documentKind="quote"
          documentId={id}
          documentStatus={header.status}
          currencySymbol={currencySymbol}
          onSuccess={() => { loadQuote(); }}
        />
        <div className="print:hidden">
          <CreditHistoryTable
            lines={viewModel?.raw_lines || viewModel?.lines || []}
            currencySymbol={currencySymbol}
          />
        </div>
        {/* EVENTSFLO-GENERIC-FINANCIAL-CREDIT-ADJUSTMENT-LINE-V1 */}
        <div className="print:hidden">
          <QuoteAdjustmentsPanel
            quoteId={id}
            quoteStatus={header.status}
            currencySymbol={currencySymbol}
            onChange={() => { loadQuote(); }}
          />
        </div>
        <div className="print:hidden">
          <EditableClientNotes
            documentKind="quote"
            documentId={id}
            initialNotes={notes}
            onSaved={loadQuote}
          />
        </div>

        {/* Quote Preview Card - EF-QUOTE-SINGLE-SOURCE-OF-TRUTH-LOCK-V1: No tenant header */}
        <Card className="overflow-hidden bg-white print:shadow-none print:rounded-none">
          {/* EF-QUOTE-UX-MICRO-IMPROVEMENTS-V1: Renamed from QUOTE to QUOTE PREVIEW */}
          <DocumentHeader
            documentType="QUOTE PREVIEW"
            documentNumber={header.document_number}
            status={header.status}
            companyProfile={null}  /* No tenant branding in header */
            headerText={null}
            client={header.client}
            clientContact={header.client_contact}
          />

          {/* Details */}
          <DocumentDetails
            documentType="quote"
            issueDate={header.issue_date}
            validUntil={header.valid_until}
            project={{ name: header.project_name }}
            client={header.client}
            formatDate={formatDate}
          />

          {/* EF-QUOTE-SINGLE-SOURCE-OF-TRUTH-LOCK-V1: Line Items from view model */}
          <GroupedLineItems
            shiftGroups={shifts}
            formatCurrency={(val) => `${currencySymbol}${(val || 0).toFixed(2)}`}
            testIdPrefix="quote-line"
          />

          {/* Totals - from view model */}
          <div className="px-6 pb-6">
            <TotalsSummary
              subtotal={totals.subtotal}
              taxRate={totals.tax_rate}
              taxAmount={totals.vat_amount}
              total={totals.total}
              currencySymbol={currencySymbol}
              taxLabel={totals.tax_label || "TVA"}
              primaryColor={footer?.primary_color || "#0d9488"}
            />
          </div>

          {/* Footer with Notes and Company Details - EF-QUOTE-SINGLE-SOURCE-OF-TRUTH-LOCK-V1 */}
          <DocumentFooter
            notes={notes}
            paymentTerms={null}
            bankDetails={null}
            footerText={null}
            validUntil={header.valid_until}
            formatDate={formatDate}
            companyProfile={footer}
          />
        </Card>

        {/* Email Log - Print hidden */}
        <div className="print:hidden">
          <EmailLogSection logs={emailLogs} loading={emailLogsLoading} />
        </div>
      </div>

      {/* Print Styles */}
      <style>{printStyles}</style>

      {/* Email Compose Modal */}
      <EmailComposeModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        documentType="quote"
        documentId={id}
        documentNumber={header.document_number}
        documentData={{
          total: totals.total,
          validUntil: formatDate(header.valid_until),
        }}
        clientId={header.client?.id}
        clientEmail={header.client_contact?.email || header.client?.contact_email}
        clientName={header.client?.name}
        companyName={footer?.company_name}
        currencySymbol={currencySymbol}
        paymentTerms={null}
        projectName={header.project_name}
        onSend={handleSendEmail}
      />
    </div>
  );
}
